import apiClient from "./apiClient";

/**
 * Fetch all leads for admin
 */
export const getAdminLeads = async (companyCode, params = {}) => {
  try {
    const res = await apiClient.get("/api/leads/admin", {
      params: { companyCode, ...params },
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[LeadService] getAdminLeads error:", err);
    return { success: false, items: [], message: err.message };
  }
};

/**
 * Fetch grouped companies with contact counts for admin
 */
export const getAdminLeadCompanies = async (companyCode, params = {}) => {
  try {
    const res = await apiClient.get("/api/leads/admin/companies", {
      params: { companyCode, ...params },
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[LeadService] getAdminLeadCompanies error:", err);
    return { success: false, companies: [], names: [], message: err.message };
  }
};

/**
 * Fetch grouped companies with contact counts for employee
 */
export const getEmployeeLeadCompanies = async (companyCode, employeeId, params = {}) => {
  try {
    const res = await apiClient.get("/api/leads/employee/companies", {
      params: { companyCode, employeeId, ...params },
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[LeadService] getEmployeeLeadCompanies error:", err);
    return { success: false, companies: [], names: [], message: err.message };
  }
};

/**
 * Fetch full company details and all associated directors/contacts
 */
export const getCompanyFullDetails = async (companyCode, companyName, employeeId = null) => {
  try {
    const params = { companyCode, companyName };
    if (employeeId) params.employeeId = employeeId;

    const res = await apiClient.get("/api/leads/company-details", {
      params,
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[LeadService] getCompanyFullDetails error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Fetch lead sets for admin
 */
export const getAdminLeadSets = async (companyCode) => {
  try {
    const res = await apiClient.get("/api/leads/admin/sets", {
      params: { companyCode },
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[LeadService] getAdminLeadSets error:", err);
    return { success: false, sets: [], message: err.message };
  }
};

/**
 * Fetch lead sets for employee
 */
export const getEmployeeLeadSets = async (companyCode, employeeId) => {
  try {
    const res = await apiClient.get("/api/leads/employee/sets", {
      params: { companyCode, employeeId },
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[LeadService] getEmployeeLeadSets error:", err);
    return { success: false, sets: [], message: err.message };
  }
};

/**
 * Fetch Single Lead Details by ID
 */
export const getLeadById = async (companyCode, leadId, options = {}) => {
  try {
    if (!leadId || !companyCode) {
      return { success: false, message: "Missing leadId or companyCode" };
    }
    const params = { companyCode, ...options };
    const res = await apiClient.get(`/api/leads/${encodeURIComponent(leadId)}`, {
      params,
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.warn("[LeadService] getLeadById:", err?.response?.data?.message || err?.message || err);
    return { success: false, message: err?.response?.data?.message || err?.message || "Failed to fetch lead" };
  }
};

/**
 * Fetch AI Brief / Company Profile
 */
export const getCompanyProfile = async (companyCode, companyName) => {
  try {
    const res = await apiClient.get("/api/leads/company-profiles", {
      params: { companyCode, companyName },
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[LeadService] getCompanyProfile error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Add / Create Lead
 */
export const createLead = async (companyCode, payload) => {
  try {
    const res = await apiClient.post(
      "/api/leads",
      { ...payload, companyCode },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[LeadService] createLead error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Update Lead Status
 */
export const updateLeadStatus = async (companyCode, leadId, status) => {
  try {
    const res = await apiClient.patch(
      `/api/leads/${leadId}/status`,
      { companyCode, status },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[LeadService] updateLeadStatus error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Update Lead Flags (isStarred, isFavourite)
 */
export const updateLeadFlags = async (companyCode, leadId, flags) => {
  try {
    const res = await apiClient.patch(
      `/api/leads/${leadId}/flags`,
      { companyCode, ...flags },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[LeadService] updateLeadFlags error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Update Lead Director Information
 */
export const updateLeadDirector = async (companyCode, leadId, directorData) => {
  try {
    const res = await apiClient.patch(
      `/api/leads/${leadId}/director`,
      { companyCode, ...directorData },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[LeadService] updateLeadDirector error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Update Lead Overview Information
 */
export const updateLeadOverview = async (companyCode, leadId, overviewData) => {
  try {
    const res = await apiClient.patch(
      `/api/leads/${leadId}/overview`,
      { companyCode, ...overviewData },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[LeadService] updateLeadOverview error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Update Lead (Intelligent Router matching Angular)
 */
export const updateLead = async (companyCode, leadId, payload) => {
  if (payload.status !== undefined) {
    return updateLeadStatus(companyCode, leadId, payload.status);
  }
  if (payload.isStarred !== undefined || payload.isFavourite !== undefined) {
    return updateLeadFlags(companyCode, leadId, payload);
  }
  if (payload.contactName !== undefined || payload.contactNumber !== undefined || payload.directorEmailAddress !== undefined) {
    return updateLeadDirector(companyCode, leadId, payload);
  }
  return updateLeadOverview(companyCode, leadId, payload);
};

/**
 * Add Remark to Lead
 */
export const addLeadRemark = async (companyCode, leadId, remarkText) => {
  try {
    const res = await apiClient.post(
      `/api/leads/${leadId}/remarks`,
      { companyCode, remark: remarkText },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[LeadService] addLeadRemark error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Fetch Assigned Leads for an Employee
 */
export const getEmployeeLeads = async (companyCode, employeeId, setLabel) => {
  try {
    const params = { companyCode, employeeId };
    if (setLabel) params.setLabel = setLabel;

    const res = await apiClient.get("/api/leads/employee", {
      params,
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[LeadService] getEmployeeLeads error:", err);
    return { success: false, items: [], leads: [], message: err.message };
  }
};

/**
 * Delete a lead / contact record
 */
export const deleteLead = async (companyCode, leadId) => {
  try {
    const res = await apiClient.delete(`/api/leads/${leadId}`, {
      params: { companyCode },
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[LeadService] deleteLead error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Bulk Import Leads from Excel Mapping
 */
export const addBulkLeads = async (companyCode, leads, options = {}) => {
  try {
    const res = await apiClient.post(
      "/api/leads/bulk",
      { leads, companyCode, ...options },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[LeadService] addBulkLeads error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Upload / Add a Company Document
 */
export const uploadCompanyDocument = async (companyCode, companyName, documentPayload) => {
  try {
    const res = await apiClient.post(
      "/api/leads/company-profiles/documents",
      { companyCode, companyName, ...documentPayload },
      { headers: companyCode ? { "x-active-company-code": companyCode } : {} }
    );
    return res;
  } catch (err) {
    console.error("[LeadService] uploadCompanyDocument error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Delete a Company Document
 */
export const deleteCompanyDocument = async (companyCode, companyName, documentId) => {
  try {
    const res = await apiClient.delete(`/api/leads/company-profiles/documents/${documentId}`, {
      params: { companyCode, companyName },
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[LeadService] deleteCompanyDocument error:", err);
    return { success: false, message: err.message };
  }
};

/**
 * Fetch company / lead history logs (remarks, status changes, activities)
 */
export const getCompanyHistory = async (companyCode, params = {}) => {
  try {
    const res = await apiClient.get("/api/history", {
      params: { companyCode, ...params },
      headers: companyCode ? { "x-active-company-code": companyCode } : {},
    });
    return res;
  } catch (err) {
    console.error("[LeadService] getCompanyHistory error:", err);
    return { success: false, logs: [], message: err.message };
  }
};
